import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PortfolioGrid = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);

  // Static list of images from public/portfolio
  const portfolioImages = [
    "1735815974153-229354721.jpg",
    "1742292736846-267879227.jpg",
    "1742292751972-5919892.jpg",
    "1742292763768-760648252.jpg",
    "1742292777647-282390011.jpg",
    "1742292787191-200632372.jpg",
    "1742292803992-388811771.jpg",
    "1742292819699-640386053.jpg",
    "1742292829151-579681759.jpg",
    "1742292866326-762049518.jpg",
    "1742292877302-235670698.jpg",
    "1742292903962-641372135.jpg",
    "1746612360007-710060961.jpg",
    "1746612510180-634546240.jpg",
    "1746612518224-591242936.jpg",
    "1746612525284-309766377.jpg",
    "1746612533032-854227167.jpg",
    "1746612540855-610484909.jpg",
    "1746612548976-360615927.jpg"
  ];

  // Mock categories for demonstration
  const mockCategories = ['EBC', 'A+ Content', 'Seller Page'];

  // Initialize projects directly
  const [projects] = useState(() =>
    portfolioImages.map((img, index) => ({
      id: index + 1,
      title: `Portfolio Item ${index + 1}`,
      // Distribute categories cyclically
      category: mockCategories[index % mockCategories.length],
      image: `/portfolio/${img}`,
      tags: [mockCategories[index % mockCategories.length]]
    }))
  );

  const [categories] = useState(['All', ...mockCategories]);

  const filteredProjects = activeFilter === 'All'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  // Modal handlers
  const openModal = (project) => {
    setSelectedProject(project);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'unset'; // Restore scrolling
  };

  return (
    <>
      <div className="md:py-24 py-12 bg-black">
        <div className="container mx-auto px-4">
          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeFilter === filter
                  ? 'bg-primary text-white'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
                  }`}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode='popLayout'>
              {filteredProjects.map((project) => (
                <motion.div
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  key={project.id}
                  className="group relative cursor-pointer"
                  onClick={() => openModal(project)}
                >
                  <div className="relative overflow-hidden rounded-2xl">
                    {/* Image */}
                    <div className="aspect-[4/3]">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover object-top transform group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          console.error('Image failed to load:', project.image);
                          e.target.onerror = null;
                        }}
                      />
                    </div>

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="space-y-3">
                          <h3 className="text-xl font-bold text-white">
                            {project.title}
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {project.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="text-xs bg-white/20 text-white/90 px-3 py-1 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Full-size Image Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-7xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center justify-center"
                onClick={closeModal}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Modal Content */}
              <div className="bg-black rounded-lg overflow-hidden">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                  }}
                />
                <div className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">
                    {selectedProject.title}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="text-sm bg-white/20 text-white/90 px-4 py-1 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PortfolioGrid;
